import { useEffect, useState } from 'react'
import { useDebounce } from '@/hooks/ui/use-debounce'
import type { SelectWithAddOption } from '@/types/select/select-with-add-option'

interface UseSelectWithAddNewProps {
  options: SelectWithAddOption[]
  onChange: (value: SelectWithAddOption) => void
}

export const useSelectWithAddNew = ({
  options,
  onChange
}: UseSelectWithAddNewProps) => {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const [localOptions, setLocalOptions] = useState(options)
  const [filter, setFilter] = useState(options)
  const termDebounce = useDebounce(term, 600)

  const handleTerm = (value: string) => {
    setTerm(value)
  }

  const handleTermFilter = (term: string) => {
    const newOptions = localOptions.filter((option) =>
      (option.label ?? '').toLowerCase().includes(term.toLowerCase())
    )
    setFilter(newOptions)
  }

  const handleChange = (option: SelectWithAddOption) => {
    onChange(option)
    setOpen(false)
  }

  const handleCreate = () => {
    if (!termDebounce.trim()) return
    const newOption = { id: `${Date.now()}`, label: termDebounce }
    setLocalOptions((prev) => [...prev, newOption])
    onChange(newOption)
    setTerm('')
    setOpen(false)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    handleTermFilter(termDebounce)
  }, [termDebounce, localOptions])

  useEffect(() => {
    setLocalOptions(options)
  }, [options])

  return {
    states: { term, filter, open },
    actions: { handleTerm, handleCreate, handleChange, setOpen }
  }
}
